/**
 * Tiny global store for "is the bug-report drawer open?" so floating
 * UI elements outside the feedback module (DM Quick Journal cluster,
 * future stat card) can react to it without prop-drilling.
 *
 * Singleton + subscriber pattern, identical to how `useUiBreadcrumb`
 * works. Set from inside SideDrawer's lifecycle effect; read via
 * `useBugReportDrawerOpen()` in App.js or anywhere else.
 *
 * Note: this is one-way — readers don't open/close the drawer, the
 * drawer publishes its state. Callers that need to dismiss the drawer
 * use the SideDrawer's onClose prop or the QuickReportButton's local
 * state.
 */

import React from 'react';

let _open = false;
const _subscribers = new Set();

export const setBugReportDrawerOpen = (next) => {
  if (_open === next) return;
  _open = next;
  for (const sub of _subscribers) {
    try { sub(_open); } catch (_) { /* swallow */ }
  }
};

export const readBugReportDrawerOpen = () => _open;

export function useBugReportDrawerOpen() {
  const [, force] = React.useReducer((n) => n + 1, 0);
  React.useEffect(() => {
    const sub = () => force();
    _subscribers.add(sub);
    return () => { _subscribers.delete(sub); };
  }, []);
  return _open;
}
